/**
 * Which games the app opens on when this device has not chosen one (or its
 * choice no longer exists):
 *
 *   1. the games running today (in its own time zone),
 *   2. else the next one to start,
 *   3. else a games whose dates are not set yet — the organisers confirm the
 *      dates before a games starts, so an undated games is a coming one,
 *   4. else the one that ended last,
 *   5. else the first.
 *
 * Ties keep the options order (main games before para), so on a day both
 * games run the main games wins. Pure: `today` is a function of the zone so
 * tests can pin it.
 */
export interface DatedGames {
	games_id: string;
	timezone: string;
	starts_on: string | null;
	ends_on: string | null;
}

/** `YYYY-MM-DD` of `now` in `timeZone`. A fixed machine locale (en-CA prints ISO dates), never the viewer's. */
export function dayIn(now: Date, timeZone: string): string {
	try {
		return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
	} catch {
		// An unknown zone from bad data: UTC is better than no default.
		return now.toISOString().slice(0, 10);
	}
}

export function defaultGames<T extends DatedGames>(games: readonly T[], now: Date): T | undefined {
	if (games.length === 0) return undefined;
	const span = (g: T) => {
		if (g.starts_on === null && g.ends_on === null) return null;
		return { start: (g.starts_on ?? g.ends_on) as string, end: (g.ends_on ?? g.starts_on) as string };
	};

	const running = games.find((g) => {
		const s = span(g);
		if (!s) return false;
		const today = dayIn(now, g.timezone);
		return s.start <= today && today <= s.end;
	});
	if (running) return running;

	let next: { g: T; start: string } | undefined;
	for (const g of games) {
		const s = span(g);
		if (s && s.start > dayIn(now, g.timezone) && (!next || s.start < next.start)) next = { g, start: s.start };
	}
	if (next) return next.g;

	const undated = games.find((g) => span(g) === null);
	if (undated) return undated;

	let last: { g: T; end: string } | undefined;
	for (const g of games) {
		const s = span(g);
		if (s && (!last || s.end > last.end)) last = { g, end: s.end };
	}
	return last?.g ?? games[0];
}
